// DOIT logo overlay — load PNG, scale, composite onto canvas.
// Ports core/renderer/doit_overlay/logo.py.

import {
  LOGO_PAD_X_FRAC,
  LOGO_PAD_Y_FRAC,
  LOGO_WIDTH_FRAC,
  MIN_WIDTH_PX,
  type Layout,
  type Variant,
} from "./constants.generated.js";
import { anchorXY, type Position } from "./positions.js";

// Logos are served from /logos/ (vite-plugin-static-copy from
// assets/logos/full-set/). Bundler doesn't transform these paths.
const LOGO_BASE_PATH = "/logos";

// Cache decoded logo ImageBitmaps so a batch doesn't re-fetch the same file.
const _logoCache = new Map<string, Promise<ImageBitmap>>();

function logoUrl(layout: Layout, variant: Variant): string {
  return `${LOGO_BASE_PATH}/${layout}_${variant}.png`;
}

async function loadLogo(layout: Layout, variant: Variant): Promise<ImageBitmap> {
  const key = `${layout}_${variant}`;
  const cached = _logoCache.get(key);
  if (cached) return cached;

  const promise = (async () => {
    const resp = await fetch(logoUrl(layout, variant));
    if (!resp.ok) {
      throw new Error(`failed to load logo ${key}: ${resp.status} ${resp.statusText}`);
    }
    const blob = await resp.blob();
    return createImageBitmap(blob);
  })();
  _logoCache.set(key, promise);
  return promise;
}

/**
 * Scale the loaded logo to LOGO_WIDTH_FRAC × short_edge, enforcing the
 * §4.2 minimum width. Returns target width/height; actual scaling is
 * done at draw-time via ctx.drawImage(...) so we don't allocate an
 * intermediate canvas.
 */
function targetLogoDims(
  canvasW: number,
  canvasH: number,
  logo: ImageBitmap,
  layout: Layout,
): { w: number; h: number } {
  const shortEdge = Math.min(canvasW, canvasH);
  const targetW = Math.max(
    MIN_WIDTH_PX[layout]!,
    Math.round(shortEdge * LOGO_WIDTH_FRAC),
  );
  const ratio = targetW / logo.width;
  const targetH = Math.max(1, Math.round(logo.height * ratio));
  return { w: targetW, h: targetH };
}

export interface OverlayLogoOptions {
  layout: Layout;
  variant: Variant;
  position: Position;
}

/**
 * Composite a DOIT logo onto an OffscreenCanvas. Returns the same canvas
 * for chaining (so caller can pipe to overlay_hashtag without re-drawing
 * the source image).
 */
export async function overlayLogo(
  canvas: OffscreenCanvas,
  ctx: OffscreenCanvasRenderingContext2D,
  opts: OverlayLogoOptions,
): Promise<OffscreenCanvas> {
  const logo = await loadLogo(opts.layout, opts.variant);
  const { w, h } = targetLogoDims(canvas.width, canvas.height, logo, opts.layout);
  const { x, y } = anchorXY(
    canvas.width,
    canvas.height,
    w,
    h,
    opts.position,
    LOGO_PAD_X_FRAC,
    LOGO_PAD_Y_FRAC,
  );
  // imageSmoothingQuality 'high' is the closest browser equivalent to PIL.LANCZOS
  // (different per browser engine — see OVERLAY_TOOL.md known drifts).
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(logo, x, y, w, h);
  return canvas;
}
